import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import Loader from '../../shared/loader/loader';
import CustomButton from '../../shared/custom-button/custom-button';
import ADDLogo from '@svg/add-logo.svg?react';
import { useStores } from '../../store';
import CustomSelect from '../../shared/custom-select/custom-select';
import './customer-selection.scss';
import { isDemoEnv } from '../../../utils/envLinksUtil';
import demoService from '../../../services/demo.service';
import usePromiseFeedback from '../../shared/promise-feedback/usePromiseFeedback';
import { LevelName } from '../../../utils/enums';
import {useLocation} from 'react-router-dom';
import { useNavigate } from 'src/utils/use-navigate';
import { ServiceDto } from '../../../dtos/customer.dto';
import { EventType } from '../../../dtos/analytics.dto';
import { isInitialPolicyModeUndefinedByService } from '../ecm/connection.model';

export const CustomerSelection: React.FC = observer(() => {
    const { customerStore, userStore } = useStores();
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();
    const navigate = useNavigate();

    const [selectedCustomer, setSelectedCustomer] = useState<number | undefined>();
    const [defaultSingleService, setDefaultSingleService] = useState<null | ServiceDto>(null);
    const [isOnlyOneCustomer, setIsOnlyOneCustomer] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const { welcomeCustomerId } = (useLocation().state ?? {});

    const loadAllCustomers = () => {
        promiseFeedbackFactory({
            promise: customerStore.load(),
            notifObj: { show: false },
            successCallback: async () => {
                if (userStore.userCustomers?.length) {
                    const onlyOneCustomerId = userStore.getTheOnlyCustomerOfExternalUser;
                    if (onlyOneCustomerId) {
                        await customerStore.loadSelectedCustomer(onlyOneCustomerId);
                        const serviceList = customerStore.selectedCustomer?.services || [];
                        navigateByServiceList(onlyOneCustomerId, serviceList);
                    } else {
                        setIsLoading(false);
                    }
                }
            }
        });
    };

    useEffect(() => {
        if (welcomeCustomerId) {
            setSelectedCustomer(welcomeCustomerId);
        }
    }, [welcomeCustomerId]);

    useEffect(() => {
        if (welcomeCustomerId && welcomeCustomerId === selectedCustomer) {
            void submitSelectedCustomer();
        }
    }, [selectedCustomer, welcomeCustomerId]);

    useEffect(() => {
        if (customerStore.isLoading) return;

        customerStore.reset();

        if (isDemoEnv()) {
            customerStore.setLoading(true);
            promiseFeedbackFactory({
                promise: demoService.createOnboardDemoData(),
                successCallback: loadAllCustomers,
                notifObj: { show: false }
            });
        } else {
            loadAllCustomers();
        }
    }, []);

    useEffect(() => {
        if (isOnlyOneCustomer) {
            customerStore.sendGeneralEvent(EventType.PageView, { pageName: 'welcomePage' });
        }
    }, [isOnlyOneCustomer]);

    const navigateByServiceList = (customerId: number, serviceList: ServiceDto[]) => {
        if (serviceList.length === 1 && serviceList[0].serviceLevel === LevelName.BRONZE) {
            navigate(`/${customerId}/${serviceList[0].serviceId}/dashboard-bronze`);
            return;
        }
        const nonBronzeServices = serviceList.filter(service => service.serviceLevel !== LevelName.BRONZE);

        if (nonBronzeServices.length === 0) {
            navigate(`/${customerId}/connections`);
            return;
        }

        if (nonBronzeServices.length === 1) {
            const singleService = nonBronzeServices[0];
            if (isInitialPolicyModeUndefinedByService(singleService)) {
                setIsOnlyOneCustomer(true);
                setDefaultSingleService(singleService);
                setSelectedCustomer(customerId);
                setIsLoading(false);
            } else if (serviceList.length === 1) {
                navigate(`/${customerId}/${singleService.serviceId}/dashboard`);
            } else {
                navigate(`/${customerId}/connections`);
            }
        } else {
            const isAllDefault = nonBronzeServices.every(service => isInitialPolicyModeUndefinedByService(service));
            if (isAllDefault) {
                setIsOnlyOneCustomer(true);
                setSelectedCustomer(customerId);
            } else {
                navigate(`/${customerId}/connections`);
            }
        }
        setIsLoading(false);
    };

    const submitSelectedCustomer = async () => {
        if (!selectedCustomer) return;
        setIsLoading(true);
        await customerStore.loadSelectedCustomer(selectedCustomer);
        const serviceList = customerStore.selectedCustomer?.services || [];
        customerStore.sendGeneralEvent(EventType.LinkClicked, { button: 'welcomePageManyCustomers' });
        navigateByServiceList(selectedCustomer, serviceList);
    };

    const goToWizardStep3 = () => {
        navigate(`/${selectedCustomer}/wizard`, {
            state: {
                initialStep: 2,
                initialData: JSON.stringify({ connection: defaultSingleService })
            }
        });
    };

    if (userStore.isLoading || customerStore.isLoading || isLoading) {
        return <Loader type="full-screen" />;
    }

    const getStarted = () => {
        customerStore.sendGeneralEvent(EventType.LinkClicked, { button: 'getStarted' });
        if (defaultSingleService) {
            goToWizardStep3();
        } else {
            navigate(`/${selectedCustomer}/wizard`);
        }
    };

    return <div className="customer-selection-page">
        <div className="add-logo"><ADDLogo/></div>
        <div className="customer-select-wrapper">
            { isOnlyOneCustomer || welcomeCustomerId ?
                <div className="customer-welcome">
                    <div className="selection-header">Welcome</div>
                    <div className="selection-content">
                        Protect your business with our built-in security that
                        stops threats before they reach you,
                        ensuring seamless connectivity.
                    </div>
                    <CustomButton isDisabled={userStore.isReadOnlyUser} label={'Get started'}
                                  className={'filter-btn get-started'} onClick={getStarted}/>
                </div> :
                <div className="customer-select">
                    <div className="selection-header">Welcome<span>Select customer to continue</span></div>
                    <div className="selection-content">
                        <CustomSelect
                            options={(userStore.userCustomers || []).map(customer => ({
                                key: customer.customerId,
                                value: customer.customerId,
                                label: customer.customerName
                            }))}
                            onSelect={(value) => setSelectedCustomer(value)}
                            placeholder="Select customer"
                            emptyMessage="No customer found"
                            value={selectedCustomer}
                            className="selection-row-customer-select"
                        />
                    </div>
                    <div className="btn-wrapper">
                        <CustomButton label={'Continue'} className={'primary-btn continue'}
                                      onClick={submitSelectedCustomer}
                                      isDisabled={!selectedCustomer} />
                    </div>
            </div>}
        </div>
        {promiseContextHolder}
    </div>;
});